import {
  ConsumptionTileElementPLP,
  DynamicWrapper,
  EnvKConsumptionAndEmissions,
  FootnoteReferenceContextProvider,
  getRelatedProduct,
  useServicesContext,
  VehicleBasic,
} from '@oneaudi/vtp-shared';
import React, { useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  AlphaOnlyWrapper,
  ConsumptionEmissionContainer,
  LinkButtonContainer,
} from './ActionButtons.styles';
import { TextLink } from '@oneaudi/unified-web-components';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { trackLayerClose, trackLayerLinkClick, trackLayerOpen } from '../../tracking/tilesTracking';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { useI18n } from '@oneaudi/i18n-context';
import { GfaLayerManagerV1 } from '@volkswagen-onehub/gfa-layer-manager';
import { FocusLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import { AudiFootnoteRefernceServiceScopeManagerInterfaceV3 } from '@oneaudi/footnote-reference-service';
import { ConsumptionEmissionLayerPlp } from './ConsumptionAndEmissionLayer/ConsumptionEmissionLayerPlp';

interface Props {
  vehicle: VehicleBasic;
}

const ConsumptionAndEmission: React.FC<Props> = ({ vehicle }) => {
  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service'
  ) as TrackingServiceV2;
  const localeService = useServicesContext().getAdditionalService(
    'gfa:locale-service'
  ) as LocaleServiceV1;

  const footnoteReferenceServiceScopeManager = useServicesContext().getAdditionalService(
    'audi-footnote-reference-service'
  ) as AudiFootnoteRefernceServiceScopeManagerInterfaceV3;

  const scopedFootnoteReferenceService =
    footnoteReferenceServiceScopeManager.getCustomScopeRefService('priceBreakDownLayer');
  const consumptionLayerLabel = useI18n({ id: 'nemo.ui.sc.tiles.consumption.link.text' });
  const isDEMarket = localeService.countryCode?.toLowerCase() === 'de';

  const relatedProduct = getRelatedProduct(vehicle);
  const layerManager = useServicesContext().getAdditionalService(
    'gfa:layer-manager'
  ) as GfaLayerManagerV1;
  const [layerNode, setLayerNode] = useState<Element | null>();
  const layerRef = useCallback(
    (node: Element | null) => {
      setLayerNode(node);
    },
    [setLayerNode]
  );
  const openLayer = () => {
    layerManager?.openFocusLayer(
      () => <div ref={layerRef} />,
      {
        withFefa: true,
      },
      {
        size: FocusLayerSizeV2.A,
        userCloseable: true,
        onClose: () => {
          if (trackingService) {
            trackLayerClose(trackingService, 'vtp emission layer');
          }
        },
      }
    );
    trackLayerLinkClick(trackingService, consumptionLayerLabel, 'text link', relatedProduct);
    trackLayerOpen(trackingService, 'emission layer', 'vtp emission layer', undefined);
  };
  const hasConsumptionValues = (veh: VehicleBasic | undefined) => {
    return (
      veh?.io?.hasWltp ||
      veh?.io?.hasNedc ||
      veh?.vlsEnergyProvision?.hasWltp ||
      veh?.vlsEnergyProvision?.hasNedc
    );
  };
  return (
    <DynamicWrapper AlphaWrapperFc={AlphaOnlyWrapper}>
      {isDEMarket && (
        <div>
          <EnvKConsumptionAndEmissions vehicle={vehicle} data-testid="pkv-envkv" isPlp />
        </div>
      )}
      {!isDEMarket && hasConsumptionValues(vehicle) && (
        <ConsumptionEmissionContainer>
          <ConsumptionTileElementPLP
            vehicleId={vehicle.id}
            order={['consumption', 'emission']}
            displayedItems={['emission', 'consumption']}
            hideLayerBtn
            isPlp
            data-testid="consumptionElement"
          />
          <LinkButtonContainer>
            <TextLink
              variant="standalone"
              size="sm"
              onClick={openLayer}
              data-testid="consumptionLayer"
            >
              {consumptionLayerLabel}
            </TextLink>
          </LinkButtonContainer>
        </ConsumptionEmissionContainer>
      )}
      {layerNode &&
        ReactDOM.createPortal(
          <div className="ConsumptionLayer">
            <FootnoteReferenceContextProvider
              footnoteReferenceServiceScopeManager={footnoteReferenceServiceScopeManager}
              customScope={scopedFootnoteReferenceService.getConsumerId()}
            >
              <ConsumptionEmissionLayerPlp vehicle={vehicle} />
            </FootnoteReferenceContextProvider>
          </div>,
          layerNode
        )}
    </DynamicWrapper>
  );
};

export default ConsumptionAndEmission;
